/**
 * session — the builder's in-memory access-token holder (build-plan §3.3, §10.2; mirrors the
 * dashboard `SessionStore`).
 *
 * Why in-memory ONLY, never localStorage/sessionStorage/IndexedDB: the access token is short-lived
 * and any web-storage copy would be exfiltrable by XSS. The long-lived refresh token lives in the
 * HttpOnly `aqx_refresh` cookie (scoped to `/api/auth`, `SameSite=Strict`), which JS cannot read; on
 * full reload the in-memory token is gone and `BuilderBoot` silently re-mints one from the cookie.
 *
 * `store_id` is read from the DECODED access-token claim ONLY — never from the URL, a header, or any
 * builder request (build-plan §3.7, §10.1). The builder never SENDS a store id; the backend derives
 * it from the same JWT. This decode is for client display/scoping convenience and is NOT a security
 * boundary — the signature is validated server-side.
 */

let accessToken: string | null = null;

export function getToken(): string | null {
  return accessToken;
}

export function setToken(token: string): void {
  if (typeof token !== 'string' || token.trim().length === 0) {
    // A blank token would flip "authenticated" true while every Bearer call 401s — refuse loudly.
    throw new Error('session.setToken: empty or non-string token rejected');
  }
  accessToken = token;
}

export function clearToken(): void {
  accessToken = null;
}

export function isAuthenticated(): boolean {
  return accessToken !== null;
}

/** Base64url → JSON (the JWT payload segment). Returns null on any malformed input. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Does the in-memory access token run out within `ms`?
 *
 * WHY (founder, 2026-09-15). An upload is not a 200 ms API call: on a home uplink a clip is minutes, and
 * a token that expires DURING one gets the whole file re-sent — `withAuthRetry` answers a 401 by
 * refreshing and replaying the request, which for an upload means every byte a second time, with the
 * progress bar still reading full and the panel still saying "Finishing up…". The seller pays twice and
 * is told nothing.
 *
 * Asking the question BEFORE the bytes leave costs one cheap refresh and removes the whole failure mode.
 * Conservative on purpose: `true` when there is no token, when `exp` is missing, or when it cannot be
 * parsed — a needless refresh is harmless, a needless re-upload is what we are here to stop.
 *
 * NOT a security boundary (nothing here is): the signature is validated server-side, and a token this
 * says is fine can still be rejected — the 401 replay stays as the backstop.
 */
export function tokenExpiresWithin(ms: number): boolean {
  if (!accessToken) return true;
  const exp = decodeJwtPayload(accessToken)?.['exp'];
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return true;
  return exp * 1000 - Date.now() <= ms;
}

/**
 * The `store_id` claim from the in-memory access token, or null when there is no token / no claim.
 * Read-only convenience; the backend is the authority (build-plan §3.7).
 */
export function getStoreId(): string | null {
  if (!accessToken) return null;
  const payload = decodeJwtPayload(accessToken);
  const claim = payload?.['store_id'];
  return typeof claim === 'string' && claim.length > 0 ? claim : null;
}
